/* The API surface, and the types the rest of the UI is written against.
   Kept in one file so that a field added on the backend is added here once and
   the compiler finds everywhere it matters. */

export type Kind = "service" | "asset";
export type Plural = "services" | "assets";

export interface Person {
  person_id: number;
  display_name: string;
  email: string | null;
  is_active: number;
  services_owned: number;
  assets_owned: number;
  tasks_owned: number;
}

export interface Maturity {
  score: number | null;
  assessed: number;
  of: number;
}

export interface Ref {
  kind: Kind;
  id: number;
  name: string;
}

export interface Dep extends Ref {
  dep_kind: string;
}

export interface TrackedObject {
  id: number;
  kind: Kind;
  name: string;
  description: string;
  class: string;
  lifecycle_stage: string;
  owner_id: number | null;
  owner_name: string | null;
  priority: string;
  /* null means not assessed. It still renders blank in a cell -- the data
     records that nobody has looked -- but it counts as zero in the average.
     See app/model.py. */
  scores: Record<string, number | null>;
  maturity: Maturity;
  contributors: { person_id: number; display_name: string }[];
  initiative_id: number | null;
  initiative: InitiativeRef | null;
  created_at: string;
  updated_at: string;
  dependencies?: { depends_on: Dep[]; dependents: Dep[] };
  tasks?: TaskSummary[];
  /* Detail only. `null` means this kind of object has no documents at all --
     assets today -- as distinct from `[]`, which means none yet. */
  links?: { link_id: number; url: string; title: string }[] | null;
  /* Detail only. The seven elements of the strategic concept, under short
     keys. `null` for assets, which do not carry one. */
  concept?: Record<string, string> | null;
}

export interface TaskSummary {
  task_id: number;
  title: string;
  status: string;
  next_milestone_date: string | null;
  owner_name: string | null;
  subtasks: number;
  subtasks_done: number;
}

export interface Task {
  id: number;
  title: string;
  description: string;
  owner_id: number | null;
  owner_name: string | null;
  next_milestone_date: string | null;
  status: string;
  subtasks: number;
  subtasks_done: number;
  /* One object or none, since migration 5. */
  attached_to: Ref | null;
  initiative_id: number | null;
  /* Which initiative this task serves, and where the answer came from.
     "inherited" follows its object and moves when the object is reassigned;
     "direct" was decided for this task and stays put. */
  initiative: (InitiativeRef & { source: "direct" | "inherited" }) | null;
  created_at: string;
  updated_at: string;
  checklist?: { subtask_id: number; text: string; is_done: number; done_at: string | null }[];
  links?: { link_id: number; url: string; title: string }[];
  events?: { event_id: number; at: string; actor: string; kind: string; detail: string }[];
}

export interface InitiativeRef {
  id: number;
  code: string;
  name: string;
}

export interface Bet {
  id: number;
  code: string;
  name: string;
  description: string;
  type: string;
  status: string;
  owner_id: number | null;
  owner_name: string | null;
  sort_order: number;
  initiative_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface Rollup {
  services: number;
  services_active: number;
  services_idea: number;
  services_retired: number;
  assets: number;
  tasks: number;
  tasks_open: number;
  tasks_in_progress: number;
  tasks_done: number;
  tasks_overdue: number;
  maturity: number | null;
  scored_objects: number;
}

export type Gap = "no_bet" | "no_work" | "all_services_idea";

export interface Initiative {
  id: number;
  code: string;
  name: string;
  description: string;
  status: string;
  owner_id: number | null;
  owner_name: string | null;
  start_quarter: string | null;
  target_quarter: string | null;
  bet_ids: number[];
  created_at: string;
  updated_at: string;
  /* Present on the overview and the detail payload, absent from the plain
     list. Always computed server-side -- see store/strategy.py. */
  rollup?: Rollup;
  gaps?: Gap[];
  services?: TrackedObject[];
  assets?: TrackedObject[];
  tasks?: Task[];
}

export interface StrategyOverview {
  bets: { bet: Bet; initiative_ids: number[]; gaps: string[] }[];
  initiatives: Initiative[];
  unlinked_initiative_ids: number[];
  totals: { bets: number; initiatives: number; initiatives_without_bet: number };
}

export interface UnmappedWork {
  services: { kind: Kind; id: number; name: string; class: string;
              lifecycle_stage: string; owner_name: string | null }[];
  assets: { kind: Kind; id: number; name: string; class: string;
            lifecycle_stage: string; owner_name: string | null }[];
  tasks: { kind: "task"; id: number; name: string; status: string;
           owner_name: string | null; next_milestone_date: string | null }[];
  total: number;
}

export interface Meta {
  lifecycle_stages: string[];
  priorities: string[];
  service_classes: string[];
  asset_classes: string[];
  dependency_kinds: string[];
  task_statuses: string[];
  service_dimensions: { key: string; label: string }[];
  asset_dimensions: { key: string; label: string }[];
  strategy_elements: { key: string; label: string; prompt: string }[];
  bet_types: string[];
  bet_statuses: string[];
  initiative_statuses: string[];
  quarters: string[];
  schema_version: number;
  auth_enabled: boolean;
  allowed_domains: string[];
  me: { email: string; role: string };
}

export interface GraphData {
  nodes: {
    kind: Kind;
    id: number;
    name: string;
    class: string;
    lifecycle_stage: string;
    priority: string;
    maturity: number | null;
  }[];
  edges: { from: { kind: Kind; id: number }; to: { kind: Kind; id: number } }[];
}

/* Carries the status and the parsed detail, because 401 and 403 mean
   different things here and the UI has a different screen for each: 401 is
   fixed by signing in, 403 with an `access` detail by somebody granting
   access, and 403 without one by having a role that may not do this. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: unknown = null,
  ) {
    super(message);
  }
}

export interface SessionUser {
  email: string;
  name: string;
  role: "admin" | "contributor" | "readonly";
  picture?: string | null;
  auth_enabled: boolean;
}

export interface AccessDenied {
  access: "no_access" | "wrong_domain";
  email: string;
  name?: string;
}

export interface AccessRow {
  email: string;
  display_name: string | null;
  role: string;
  status: string;
  added_by: string | null;
  added_at: string;
  last_seen_at: string | null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    /* The backend answers a rejected write with {detail: "priority must be one
       of: ..."}. Surfacing that sentence is the whole point of validating in
       the store layer rather than letting a CHECK constraint fail. */
    let message = `${response.status} ${response.statusText}`;
    let detail: unknown = null;
    try {
      const payload = await response.json();
      detail = payload?.detail ?? null;
      if (typeof detail === "string") message = detail;
    } catch {
      /* not JSON; keep the status line */
    }
    throw new ApiError(message, response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export const api = {
  me: () => request<SessionUser>("GET", "/api/auth/me"),
  signOut: () => request<unknown>("POST", "/api/auth/logout"),

  access: () => request<{ roles: string[]; users: AccessRow[] }>("GET", "/api/access"),
  grant: (email: string, role: string) =>
    request<AccessRow>("POST", "/api/access", { email, role }),
  changeAccess: (email: string, body: Record<string, unknown>) =>
    request<AccessRow>("PATCH", `/api/access/${encodeURIComponent(email)}`, body),
  revoke: (email: string) =>
    request<unknown>("DELETE", `/api/access/${encodeURIComponent(email)}`),

  meta: () => request<Meta>("GET", "/api/meta"),
  people: () => request<Person[]>("GET", "/api/people"),
  addPerson: (display_name: string) =>
    request<Person>("POST", "/api/people", { display_name }),
  patchPerson: (id: number, body: Record<string, unknown>) =>
    request<Person>("PATCH", `/api/people/${id}`, body),
  removePerson: (id: number) => request<unknown>("DELETE", `/api/people/${id}`),

  list: (plural: Plural) => request<TrackedObject[]>("GET", `/api/${plural}`),
  get: (plural: Plural, id: number) => request<TrackedObject>("GET", `/api/${plural}/${id}`),
  create: (plural: Plural, body: Record<string, unknown>) =>
    request<TrackedObject>("POST", `/api/${plural}`, body),
  patch: (plural: Plural, id: number, body: Record<string, unknown>) =>
    request<TrackedObject>("PATCH", `/api/${plural}/${id}`, body),
  remove: (plural: Plural, id: number) => request<unknown>("DELETE", `/api/${plural}/${id}`),

  addObjectLink: (plural: Plural, id: number, url: string, title: string) =>
    request<TrackedObject>("POST", `/api/${plural}/${id}/links`, { url, title }),
  removeObjectLink: (plural: Plural, id: number, linkId: number) =>
    request<TrackedObject>("DELETE", `/api/${plural}/${id}/links/${linkId}`),

  addDependency: (plural: Plural, id: number, to_kind: Kind, to_id: number, kind: string) =>
    request<{ cycle: { kind: Kind; id: number }[] | null; object: TrackedObject }>(
      "POST",
      `/api/${plural}/${id}/dependencies`,
      { to_kind, to_id, kind },
    ),
  removeDependency: (plural: Plural, id: number, to_kind: Kind, to_id: number) =>
    request<{ object: TrackedObject }>(
      "DELETE",
      `/api/${plural}/${id}/dependencies/${to_kind}/${to_id}`,
    ),

  tasks: () => request<Task[]>("GET", "/api/tasks"),
  task: (id: number) => request<Task>("GET", `/api/tasks/${id}`),
  createTask: (body: Record<string, unknown>) => request<Task>("POST", "/api/tasks", body),
  patchTask: (id: number, body: Record<string, unknown>) =>
    request<Task>("PATCH", `/api/tasks/${id}`, body),
  removeTask: (id: number) => request<unknown>("DELETE", `/api/tasks/${id}`),
  addSubtask: (id: number, text: string) =>
    request<Task>("POST", `/api/tasks/${id}/subtasks`, { text }),
  setSubtask: (subtaskId: number, is_done: boolean) =>
    request<Task>("PATCH", `/api/subtasks/${subtaskId}`, { is_done }),
  removeSubtask: (subtaskId: number) => request<Task>("DELETE", `/api/subtasks/${subtaskId}`),
  addLink: (id: number, url: string, title: string) =>
    request<Task>("POST", `/api/tasks/${id}/links`, { url, title }),
  removeLink: (linkId: number) => request<Task>("DELETE", `/api/links/${linkId}`),

  bets: () => request<Bet[]>("GET", "/api/bets"),
  bet: (id: number) => request<Bet>("GET", `/api/bets/${id}`),
  createBet: (body: Record<string, unknown>) => request<Bet>("POST", "/api/bets", body),
  patchBet: (id: number, body: Record<string, unknown>) =>
    request<Bet>("PATCH", `/api/bets/${id}`, body),
  removeBet: (id: number) => request<unknown>("DELETE", `/api/bets/${id}`),
  reorderBets: (order: number[]) => request<Bet[]>("POST", "/api/bets/order", { order }),

  initiatives: () => request<Initiative[]>("GET", "/api/initiatives"),
  initiative: (id: number) => request<Initiative>("GET", `/api/initiatives/${id}`),
  createInitiative: (body: Record<string, unknown>) =>
    request<Initiative>("POST", "/api/initiatives", body),
  patchInitiative: (id: number, body: Record<string, unknown>) =>
    request<Initiative>("PATCH", `/api/initiatives/${id}`, body),
  removeInitiative: (id: number) => request<unknown>("DELETE", `/api/initiatives/${id}`),

  strategy: () => request<StrategyOverview>("GET", "/api/strategy"),
  unmapped: () => request<UnmappedWork>("GET", "/api/unmapped"),

  graph: () => request<GraphData>("GET", "/api/graph"),

  /* Replaces everything. The server takes a timestamped copy of the current
     database first and returns where it put it. */
  importBackup: (payload: unknown) =>
    request<{
      schema_version: number;
      loaded: Record<string, number>;
      ignored_columns: Record<string, string[]>;
      /* Tables the file had and this schema does not, and what was done about
         them. Non-empty only when restoring a backup older than the migration
         that retired one. */
      retired: string[];
      upgraded: string[];
      previous_database_saved_to: string | null;
    }>("POST", "/api/import", payload),
};

export const plural = (kind: Kind): Plural => (kind === "service" ? "services" : "assets");
